/**
 * Keeps the lens effects off the cards.
 *
 * Bloom and chromatic aberration are lens effects, and they are right for the
 * felt, the rails, the dust and every flare. They are wrong for a card: the
 * bloom's composite lays a soft glow and a highlight shoulder over a face whose
 * whole job is to be read, and the aberration's five sub-pixel taps blur it. At
 * `medium` and `high` that cost a card about a third of its sharpness, and at
 * `low` — which has neither — none. So the player's cards were soft until the
 * governor, a few seconds in, demoted the tier, and then they snapped into
 * focus. The governor was the thing that sharpened the cards.
 *
 * ## How
 *
 * The scene still goes through the chain whole, because the chain's *inputs*
 * are right: a bonus flare should be lit by the cards and the particles, the
 * glow should spill out from behind a card's edge. What must not happen is the
 * glow landing on the card's own pixels. So:
 *
 * 1. The card layer sits in a group of its own with a {@link CardCaptureFilter}.
 *    The filter passes the cards through untouched (they are composited into
 *    the scene exactly as before, in the same place in the z-order) and, as a
 *    side effect, keeps their coverage as a half-resolution alpha mask.
 * 2. Bloom and aberration read that mask and, where it is set, return the pixel
 *    they were *given* rather than the one they computed. A card pixel — and
 *    anything drawn over it, because the pixel they were given is the finished
 *    scene — leaves the pair untouched, bit for bit. The felt around it gets the
 *    full treatment.
 *
 * The vignette, grain and grade still see the cards: they are the print, not
 * the lens, and they are the same in every tier. That is what makes the tier
 * invisible to a card — `high`, `medium` and `low` run identical passes over
 * its pixels.
 *
 * The capture costs one extra render target and two blits (the second at a
 * quarter of the pixels), and only exists while bloom or aberration does: at
 * `low` the filter is disabled, Pixi skips the group, and the layer draws
 * straight into the scene as it always did.
 */

import { Filter, GlProgram, Texture, TexturePool, defaultFilterVert } from 'pixi.js';
import type { FilterSystem, RenderSurface } from 'pixi.js';

/** The mask is a coverage edge, not a picture: a quarter of the pixels is plenty. */
const MASK_SCALE = 0.5;

const copyFragment = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;

void main(void) {
    finalColor = texture(uTexture, vTextureCoord);
}
`;

const coverageFragment = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;

void main(void) {
    finalColor = vec4(texture(uTexture, vTextureCoord).a);
}
`;

function program(fragment: string, name: string): GlProgram {
  return GlProgram.from({ vertex: defaultFilterVert, fragment, name, preferredFragmentPrecision: 'highp' });
}

/** The uniforms a consumer declares to read the mask. See {@link CardShield.bind}. */
export interface ShieldUniforms {
  uMaskScale: Float32Array;
  uMaskMax: Float32Array;
  uShield: number;
}

/** GLSL for a filter that honours the shield: declarations, and the sample. */
export const GLSL_SHIELD = /* glsl */ `
uniform sampler2D uCardMask;
uniform vec2 uMaskScale;
uniform vec2 uMaskMax;
uniform float uShield;

/** 1 where a card is and the effect must stand down, 0 elsewhere. */
float shielded(vec2 tc) {
    vec2 muv = min(tc * uMaskScale, uMaskMax);
    return texture(uCardMask, muv).r * uShield;
}
`;

/** The resource and uniform entries a consumer adds to its own, so they cannot drift apart. */
export function shieldResources(): {
  uCardMask: Texture['source'];
  shieldUniforms: { uMaskScale: { value: Float32Array; type: 'vec2<f32>' }; uMaskMax: { value: Float32Array; type: 'vec2<f32>' }; uShield: { value: number; type: 'f32' } };
} {
  return {
    uCardMask: Texture.EMPTY.source,
    shieldUniforms: {
      uMaskScale: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
      uMaskMax: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
      uShield: { value: 0, type: 'f32' },
    },
  };
}

/**
 * The mask, and the filter that makes it. One per post chain.
 *
 * `strength` is how completely a card is protected, 0..1; it is 1 for the game
 * and is a knob only so a caller can ease the shield off for an effect that is
 * *meant* to wash over the cards.
 */
export class CardShield {
  readonly capture: CardCaptureFilter;

  /** Set while the capture filter has produced a mask this frame. */
  mask: Texture | null = null;
  strength = 1;

  constructor() {
    this.capture = new CardCaptureFilter(this);
  }

  /**
   * Point a consumer's resources at the current mask.
   *
   * Called from the consumer's own `apply`, immediately before its final
   * `applyFilter`, with the *input* it is about to read — the mask is mapped
   * through that texture's UV space, so it is right whatever padding the pool
   * gave the input.
   */
  bind(
    filter: Filter,
    uniforms: ShieldUniforms,
    input: Texture,
  ): void {
    const mask = this.mask;
    if (!mask || this.strength <= 0) {
      uniforms.uShield = 0;
      (filter.resources as Record<string, unknown>).uCardMask = Texture.EMPTY.source;
      return;
    }
    const source = mask.source;
    const halfTexelX = 0.5 / source.pixelWidth;
    const halfTexelY = 0.5 / source.pixelHeight;
    uniforms.uMaskScale[0] = input.source.width / source.width;
    uniforms.uMaskScale[1] = input.source.height / source.height;
    uniforms.uMaskMax[0] = mask.frame.width / source.width - halfTexelX;
    uniforms.uMaskMax[1] = mask.frame.height / source.height - halfTexelY;
    uniforms.uShield = this.strength;
    (filter.resources as Record<string, unknown>).uCardMask = source;
  }

  /** Let go of the mask: the capture is off, so nothing may read a stale one. */
  release(): void {
    if (this.mask) TexturePool.returnTexture(this.mask);
    this.mask = null;
  }

  destroy(): void {
    this.release();
    this.capture.destroy();
  }
}

/**
 * Goes on the card group. Draws what it is given, unchanged, and remembers
 * where the cards were.
 */
export class CardCaptureFilter extends Filter {
  readonly #shield: CardShield;
  readonly #coverage: Filter;

  constructor(shield: CardShield) {
    super({
      glProgram: program(copyFragment, 'wild21-card-capture'),
      resources: {},
      // The cards were drawn into an antialiased target before this existed;
      // keep their edges exactly as they were.
      resolution: 'inherit',
      antialias: 'inherit',
      padding: 0,
    });
    this.#shield = shield;
    this.#coverage = new Filter({
      glProgram: program(coverageFragment, 'wild21-card-coverage'),
      resources: {},
      resolution: 'inherit',
      antialias: 'off',
    });
  }

  override apply(
    filterManager: FilterSystem,
    input: Texture,
    output: RenderSurface,
    clearMode: boolean,
  ): void {
    // Their outline first, for the lens effects further down the chain...
    this.#shield.release();
    const mask = TexturePool.getOptimalTexture(
      input.frame.width,
      input.frame.height,
      input.source.resolution * MASK_SCALE,
      false,
    );
    filterManager.applyFilter(this.#coverage, input, mask, true);
    this.#shield.mask = mask;

    // ...and the cards, into the scene, as if this filter were not here. Last, because whatever a filter
    // draws last is the target the renderer is left bound to: drawing the mask after this would leave the
    // rest of the frame (the totals, the particles) rendering into it.
    filterManager.applyFilter(this, input, output, clearMode);
  }

  override destroy(): void {
    this.#coverage.destroy();
    super.destroy();
  }
}
